import { Inject, Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import {
  Observable,
  catchError,
  defer,
  from,
  map,
  of,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import { AUDIO_CONTEXT } from './token';

const READ_ERROR = 'AUDIO_READ_ERROR';

@Injectable({
  providedIn: 'root',
})
export class ExternalAudioValidationService {
  private decodeCache = new WeakMap<File, Observable<AudioBuffer>>();

  constructor(@Inject(AUDIO_CONTEXT) private audioContext: AudioContext) {}

  decode(audio: File): Observable<AudioBuffer> {
    const cached = this.decodeCache.get(audio);

    if (cached) {
      return cached;
    }

    const decoded$ = defer(() => from(audio.arrayBuffer())).pipe(
      catchError(() => throwError(() => new Error(READ_ERROR))),
      switchMap((buffer) => this.audioContext.decodeAudioData(buffer)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.decodeCache.set(audio, decoded$);

    return decoded$;
  }

  validate(audio: File): Observable<ValidationErrors | null> {
    if (!audio) {
      return of({ invalidContent: true });
    }

    return this.decode(audio).pipe(
      map(() => null),
      catchError((error) =>
        of(
          error?.message === READ_ERROR
            ? { readError: true }
            : { invalidContent: true }
        )
      )
    );
  }
}
